/**
 * The plants this user may pick, and the one they have picked — reconciled against each other.
 *
 * The selection is remembered in localStorage, and `me.facilityIds` is `null` for a head-office
 * user, so there is nothing on the principal to validate it against. Without this the browser will
 * happily keep a plant that no longer exists — after a database is rebuilt, or when the API is
 * pointed somewhere else — and the first thing that notices is the server, answering
 * `facility not found` to whatever the operator was trying to do.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAuth } from "@/features/auth/store";
import { api } from "@/lib/api-client";

export interface Facility {
  id: number;
  plantCode: string;
  name: string;
  /** "external" is a dryer; absent from a response cached before the API sent it. */
  type?: "internal" | "external" | undefined;
}

export function useFacilities() {
  const me = useAuth((s) => s.me);
  const facilityId = useAuth((s) => s.facilityId);
  const setFacility = useAuth((s) => s.setFacility);

  // /me/facilities, not the Processing Plant master: picking your plant is part of being signed in.
  // A warehouse operator holds no masters.* code, so gating this on one left their switcher empty
  // and stopped their single plant being auto-selected. The server already scopes the list, so
  // there is nothing left to filter here either.
  const q = useQuery({
    queryKey: ["me", "facilities"],
    queryFn: () => api<{ items: Facility[] }>("/me/facilities"),
    enabled: !!me,
    staleTime: 5 * 60_000,
    select: (d) => d.items,
  });

  const facilities = q.data;
  const selected =
    facilityId === null
      ? null
      : (facilities?.find((f) => f.id === facilityId) ?? null);

  useEffect(() => {
    if (!facilities) return;
    // A remembered plant that is no longer on the list is dropped, not shown as a ghost.
    if (facilityId !== null && !facilities.some((f) => f.id === facilityId)) {
      setFacility(facilities.length === 1 ? facilities[0]!.id : null);
      return;
    }
    // One plant is not a choice; pick it so nothing has to ask.
    if (facilityId === null && facilities.length === 1)
      setFacility(facilities[0]!.id);
  }, [facilities, facilityId, setFacility]);

  return {
    facilities: facilities ?? [],
    /** The chosen plant, or null for "All plants" / nothing chosen yet. */
    selected,
    facilityId: selected?.id ?? null,
    /** The list has been fetched, so `facilityId` can be trusted. */
    ready: !q.isPending || !me,
    isLoading: q.isPending,
  };
}
